import { useSignal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import type { NotMePreview } from '@amumin/api-client';
import { t } from '@amumin/i18n';
import { Button, Notice } from '@amumin/ui';

import { api, errorMessage, isApiError } from '../api.js';
import { AuthLayout, tokenFromUrl } from '../components/AuthLayout.js';
import { messages } from '../state/session.js';

/**
 * "Dit was ik niet" (ONTWERP.md §11.1.2): de landingspagina toont zonder bijwerking wat er gaat
 * gebeuren (hoeveel vertrouwde browsers en sessies het raakt) en de knop doet de POST.
 */
export function NotMePage() {
  const { query } = useLocation();
  const m = messages.value;
  const token = tokenFromUrl(query);
  const preview = useSignal<NotMePreview | null>(null);
  const previewFailed = useSignal(false);
  const error = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);
  const done = useSignal(false);

  useEffect(() => {
    if (!token) return;
    api.app.auth
      .notMePreview(token)
      .then((p) => (preview.value = p))
      .catch((e: unknown) => {
        previewFailed.value = true;
        if (isApiError(e, 400) || isApiError(e, 404)) error.value = m.web.notMeInvalid;
      });
  }, [token]);

  const submit = async () => {
    if (!token) return;
    busy.value = true;
    error.value = undefined;
    try {
      await api.app.auth.notMe(token);
      done.value = true;
    } catch (e) {
      error.value =
        isApiError(e, 400) || isApiError(e, 404) ? m.web.notMeInvalid : errorMessage(e, m);
    } finally {
      busy.value = false;
    }
  };

  const p = preview.value;

  return (
    <AuthLayout title={m.web.notMeTitle}>
      {!token ? <Notice kind="error">{m.web.tokenMissing}</Notice> : null}
      {error.value ? <Notice kind="error">{error.value}</Notice> : null}
      {done.value ? (
        <Notice kind="success" name="not-me-done">
          {m.web.notMeDone}
        </Notice>
      ) : (
        <div class="stack">
          <p>{m.web.notMeIntro}</p>
          <div data-name="not-me-preview">
            <p class="muted small">{m.web.notMeWhatHappens}</p>
            <ul>
              <li>{m.web.notMeStep1}</li>
              <li>
                {p ? t(m.web.notMeStep2, { count: p.trusted_browsers }) : m.web.notMeStep2Unknown}
              </li>
              {p && p.open_challenges > 0 ? (
                <li>{t(m.web.notMeStep3, { count: p.open_challenges })}</li>
              ) : null}
              <li>{m.web.notMeStep4}</li>
            </ul>
          </div>
          <Button
            name="not-me"
            variant="danger"
            block
            busy={busy.value}
            disabled={!token || (previewFailed.value && Boolean(error.value))}
            onPress={() => void submit()}
          >
            {m.web.notMeButton}
          </Button>
        </div>
      )}
      <p class="auth-links">
        <a href="/login">{m.web.toLogin}</a>
      </p>
    </AuthLayout>
  );
}
